// FREO: Uses overwrightColorMode override pattern that bypasses Chakra's theme context; raw scale tokens required for deterministic per-instance mode.
import { useAuth0 } from "@auth0/auth0-react";
import { Badge } from "@chakra-ui/react/badge";
import { Button } from "@chakra-ui/react/button";
import { useBreakpointValue } from "@chakra-ui/react/hooks";
import { Icon } from "@chakra-ui/react/icon";
import { HStack } from "@chakra-ui/react/stack";
import { Text } from "@chakra-ui/react/text";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar } from "components/ui/avatar";
import { useColorMode } from "components/ui/color-mode";
import { toaster } from "components/ui/toaster";
import { AgentMerchantDomain } from "entities/domain/agents/agent-merchant-domain";
import { useCurrentAgent } from "hooks/queries/use-current-agent";
import {
  merchantQueryKey,
  useMerchantQuery,
} from "hooks/queries/use-settings-merchant";
import useShortcut from "hooks/use-key-press";
import React, { useMemo } from "react";
import { LuCheck } from "react-icons/lu";
import { batch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setMerchantId } from "redux/features/merchant";
import { GROUP_SLICES } from "redux/groupSlices";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { MERCHANT_SLICES } from "redux/merchantSlices";
import MerchantService from "services/merchant";

interface DesktopSwitchButtonProps {
  merchantOption: AgentMerchantDomain;
  unreadCount?: number;
  index: number;
  size?: "sm" | "md" | "lg";
  overwrightColorMode?: "light" | "dark";
  buttonColorScheme?: string;
}

const DesktopSwitchButton = ({
  merchantOption,
  unreadCount = 0,
  index,
  size = "sm",
  buttonColorScheme,
  overwrightColorMode,
}: DesktopSwitchButtonProps) => {
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const auth0Context = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { colorMode } = useColorMode();
  const { colorScheme } = useAppSelector((state) => state.theme);
  const { data: merchant } = useMerchantQuery();
  const { data: currentAgent } = useCurrentAgent();

  const loadMerchant = async (
    id: number,
    previousGroupId: string
  ): Promise<void> => {
    try {
      const merchantResponse = await MerchantService.getMerchant(
        auth0Context,
        id
      );

      if (!merchantResponse) {
        toaster.create({
          type: "error",
          title:
            "Error switching merchant, please refresh the page and try again!",
        });

        return;
      }

      const hasGroupChanged = previousGroupId !== merchantResponse.groupId;

      batch(() => {
        MERCHANT_SLICES.forEach((slice) => {
          dispatch(slice.resetStore());
        });

        if (hasGroupChanged) {
          GROUP_SLICES.forEach((slice) => {
            dispatch(slice.resetStore());
          });
        }
      });

      // Update TanStack Query cache first
      queryClient.setQueryData(merchantQueryKey(id), merchantResponse);

      const pathParts = location.pathname.split("/").filter((p) => !!p);
      const returnTo = pathParts[1] || "";

      // Navigate BEFORE updating Redux/localStorage to prevent race conditions:
      // 1. URL changes first so MerchantPrivateRoute sees the correct merchantId in useParams
      // 2. Then Redux updates - when components re-render, URL is already correct
      navigate(`/${id}/${returnTo}`);

      localStorage.setItem("fuzey:merchant:id", id.toString());

      // Set merchant ID in Redux LAST (for hooks that depend on it)
      // This triggers re-renders, but by now URL and cache are already updated
      dispatch(
        setMerchantId({
          id: merchantResponse.id,
          country: merchantResponse.country,
        })
      );
    } catch (err) {
      // eslint-disable-next-line
      console.error("Error fetching merchant", err);

      toaster.create({
        type: "error",
        title: "Error switching merchant, please try again later!",
      });
    }
  };

  const onKeyPress = () => {
    // Skip if already on this merchant
    if (merchant && merchantOption.id === merchant.id) {
      return;
    }

    loadMerchant(merchantOption.id, merchant?.groupId || "");
  };

  const shortcut = useShortcut(
    index,
    merchantOption.name,
    onKeyPress,
    currentAgent?.merchants?.length || 1
  );

  const leftIcon = useMemo(() => {
    return (
      <HStack justifyContent="start" alignItems="center" gap={1}>
        <Avatar
          size="xs"
          name={merchantOption.name}
          src={merchantOption.logo || undefined}
        />
      </HStack>
    );
  }, [merchantOption.name, merchantOption.logo]);

  const rightIcon = useMemo(() => {
    if (merchant && merchantOption.id === merchant.id) {
      return (
        <Icon
          as={LuCheck}
          color={{
            _light: `${colorScheme}.500`,
            _dark: `${colorScheme}.200`,
          }}
        />
      );
    }

    if (unreadCount) {
      return <Badge colorPalette="red">{unreadCount}</Badge>;
    } else if (!isBaseSize) {
      return <Text>{shortcut}</Text>;
    }
  }, [
    merchantOption.id,
    merchant?.id,
    unreadCount,
    shortcut,
    isBaseSize,
    colorScheme,
  ]);

  const isCurrentMerchant = merchant && merchantOption.id === merchant.id;

  return (
    <Button
      flexShrink={0}
      pointerEvents={isCurrentMerchant ? "none" : "auto"}
      colorPalette={buttonColorScheme || colorScheme}
      _hover={{
        textDecorationLine: "none",
        bgColor: "whiteAlpha.100",
      }}
      variant={
        isCurrentMerchant ? "subtle" : !buttonColorScheme ? "ghost" : "solid"
      }
      bgColor={
        !buttonColorScheme
          ? (overwrightColorMode || colorMode) === "dark"
            ? `${colorScheme}.800`
            : `${colorScheme}.50`
          : (overwrightColorMode || colorMode) === "dark"
            ? `${buttonColorScheme}.800`
            : `${buttonColorScheme}.50`
      }
      color={
        !buttonColorScheme
          ? "fg.default"
          : (overwrightColorMode || colorMode) === "dark"
            ? `${buttonColorScheme}.50`
            : `${buttonColorScheme}.800`
      }
      onClick={() => {
        // Pass empty string if no current merchant - this will trigger GROUP_SLICES reset
        // since hasGroupChanged will be true (empty string !== any groupId)
        loadMerchant(merchantOption.id, merchant?.groupId || "");
      }}
      key={merchantOption.id}
      w="100%"
      display="flex"
      size={size}
      justifyContent="space-between"
      pl={2}
    >
      {leftIcon}
      <Text
        flexGrow={1}
        textAlign="left"
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
        color="inherit"
      >
        {currentAgent?.getGroupName()
          ? merchantOption.getNameWithoutGroup()
          : merchantOption.name}
      </Text>
      {rightIcon}
    </Button>
  );
};

export default DesktopSwitchButton;
